import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const processSteps = [
  {
    number: "01",
    title: "Discovery + Lean Pod",
    description: "Every engagement begins with a short discovery that is unapologetically practical. We sit with users and operators, surface constraints early, and agree on the outcomes that will define success. We staff a lean senior pod and deliver early."
  },
  {
    number: "02", 
    title: "Your own reusable Flux Engine",
    description: "What works becomes a reusable “Flux Engine”: agent templates, retrieval pipelines, evaluation and safety harnesses, governance checklists, and SLSA‑aligned build chains. We maintain weekly demos, living documentation, and clear roadmaps."
  },
  {
    number: "03",
    title: "Capability transfer", 
    description: "We design every engagement to transfer capabilities, not create dependency. We guarantee that you will operate with evidence that will allow you to ship, learn, and iterate with real‑world feedback."
  }
];

const WorkProcessSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden text-white" id="work-process" style={{background: 'linear-gradient(to right, #000000, #B85C38)'}}>
      <div className="container mx-auto py-16 lg:py-24 px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h6 className="mb-3 text-white/80 text-lg font-medium">Data-driven methodologies.</h6>
          <h1 className="text-4xl lg:text-5xl font-medium">Our work process</h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative mb-8 lg:mb-0"
            >
              <span className="absolute left-1/2 top-5 transform -translate-x-1/2 text-7xl lg:text-9xl font-medium opacity-25 leading-none">
                {step.number}
              </span>
              <div className="relative pt-16 lg:pt-24">
                <h5 className="text-xl font-semibold mb-4">{step.title}</h5>
                <p className="text-white/75 leading-relaxed max-w-xs">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkProcessSection;